import React, { useState } from "react";
import { Alert, Button, Form, Typography } from "antd";
import { MailOutlined } from "@ant-design/icons";
import { requestEnrolmentEmailCode } from "../../../api/auth/mfaApi";
import { authFailure, authFailureMessage } from "../../../lib/http/authErrors";
import type { EnrolmentCodeSent } from "../../../types/auth/mfa";
import OtpInput from "../../../components/auth/mfa/OtpInput";
import { isOtp } from "../../../components/auth/mfa/codes";

const { Text } = Typography;

interface EnrolmentEmailCodeProps {
	onNotRequired: () => void;
	disabled?: boolean;
}

// The `email_code` field of a staff user's first factor (contract §6.6); must sit inside the enrolment Form.
const EnrolmentEmailCode: React.FC<EnrolmentEmailCodeProps> = ({ onNotRequired, disabled }) => {
	const [sent, setSent] = useState<EnrolmentCodeSent | null>(null);
	const [sending, setSending] = useState(false);
	const [error, setError] = useState("");

	const send = async () => {
		setSending(true);
		setError("");
		try {
			setSent(await requestEnrolmentEmailCode());
		} catch (err) {
			const failure = authFailure(err);
			if (failure.code === "conflict" && failure.details.reason === "email_code_not_required") {
				onNotRequired();
			} else if (failure.code === "no_deliverable_email") {
				setError("Your account has no email address that can receive codes. Ask a clinic administrator to correct it.");
			} else {
				setError(authFailureMessage(err));
			}
		} finally {
			setSending(false);
		}
	};

	return (
		<div data-testid="security-email-code">
			<Text type="secondary">Your first sign-in method also needs a code sent to your account email.</Text>
			<div style={{ margin: "var(--mg-sp-4) 0" }}>
				<Button data-testid="security-email-code-send" icon={<MailOutlined />} onClick={send} loading={sending} disabled={disabled}>
					{sent ? "Send another code" : "Email me a code"}
				</Button>
				{sent && (
					<Text data-testid="security-email-code-sent" type="secondary" style={{ marginLeft: "var(--mg-sp-4)" }}>
						Sent to {sent.masked_destination}
					</Text>
				)}
			</div>
			{error && <Alert data-testid="security-email-code-error" type="error" showIcon message={error} style={{ marginBottom: "var(--mg-sp-4)" }} />}
			<Form.Item
				name="email_code"
				label="Code from your email"
				rules={[{ validator: (_, value) => (isOtp(value ?? "") ? Promise.resolve() : Promise.reject(new Error("Enter the 6-digit code from your email"))) }]}
			>
				<OtpInput data-testid="security-email-code-input" autoComplete="off" disabled={disabled} />
			</Form.Item>
		</div>
	);
};

export default EnrolmentEmailCode;
